import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  FIND_LOG_DISK_DEBOUNCE_MS,
  FIND_LOG_PREFERENCES_KEY,
} from '../../config/findRemoteLogging';

let latestSnapshotJson = null;
let debounceTimer = null;
let writeChain = Promise.resolve();

function isNative() {
  return Capacitor.isNativePlatform();
}

export async function restorePersistedEntries() {
  if (!isNative()) {
    return [];
  }
  try {
    const { value } = await Preferences.get({ key: FIND_LOG_PREFERENCES_KEY });
    if (!value) {
      return [];
    }
    const parsed = JSON.parse(value);
    return Array.isArray(parsed?.entries) ? parsed.entries : [];
  } catch {
    return [];
  }
}

function scheduleDebouncedPersist() {
  if (!isNative() || !latestSnapshotJson) {
    return;
  }
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushPersistToDisk('debounced');
  }, FIND_LOG_DISK_DEBOUNCE_MS);
}

async function flushPersistToDisk() {
  if (!isNative()) {
    return;
  }
  const payload = latestSnapshotJson;
  if (!payload) {
    return;
  }
  writeChain = writeChain
    .then(async () => {
      const current = latestSnapshotJson;
      if (!current || current !== payload) {
        return;
      }
      await Preferences.set({ key: FIND_LOG_PREFERENCES_KEY, value: current });
    })
    .catch(() => {
      /* disk persist is best-effort */
    });
  await writeChain;
}

/** Update pending tail — debounced write always serializes latest state at execution time. */
export function persistPendingEntries(entries) {
  if (!isNative()) {
    return;
  }
  latestSnapshotJson = JSON.stringify({
    version: 1,
    savedAt: Date.now(),
    entries,
  });
  scheduleDebouncedPersist();
}

/** Immediate persist for app background — no stale debounced overwrite. */
export function persistPendingEntriesNow(entries) {
  if (!isNative()) {
    return Promise.resolve();
  }
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  latestSnapshotJson = JSON.stringify({
    version: 1,
    savedAt: Date.now(),
    entries,
  });
  return flushPersistToDisk('background');
}

export async function clearPersistedEntries() {
  if (!isNative()) {
    return;
  }
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  latestSnapshotJson = null;
  try {
    await Preferences.remove({ key: FIND_LOG_PREFERENCES_KEY });
  } catch {
    /* ignore */
  }
}
